import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { StartDrill } from '@/components/training/StartDrill';
import { loadTrainingDrill, type TrainingDrill } from '@/lib/training-program/drill-loader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * LibraryStartDrillPage - Start drill page for the Drill Library context
 * 
 * UX-AUDIT-V3-100: This page loads drills directly from the drill loader,
 * allowing users to start ANY drill from the library.
 * 
 * UX-AUDIT-V3-101: Tracks origin context to return to library after execution.
 */
export default function LibraryStartDrillPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState<TrainingDrill | null>(null);

  // Track origin for return-to-origin after execution
  const fromContext = (location.state as { from?: string })?.from || 'library';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!slug) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const loadedDrill = await loadTrainingDrill(slug);
        if (!cancelled) {
          setDrill(loadedDrill);
        }
      } catch (error) {
        console.error('Failed to load drill:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleBack = () => {
    navigate(`/drills/${slug}`);
  };

  const handleStartExecution = (drillId: string) => {
    // Pass library context so DrillExecutionPage knows where to return
    navigate(`/drills/${drillId}/execute`, { state: { from: fromContext } });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!drill) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-8 space-y-3">
            <h2 className="text-xl text-[#0D382A] font-semibold">Drill Not Found</h2>
            <p className="text-sm text-gray-600">This drill could not be loaded.</p>
            <Button onClick={() => navigate('/drills')} className="w-full">
              Back to Drill Library
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <StartDrill
      drill={drill}
      onBack={handleBack}
      onStartExecution={handleStartExecution}
    />
  );
}
